import { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGasStation } from '../GasStationsLayout';
import { listOrders } from '@vn-propane/shared/client';
import type { OrderSummary, OrderStatus } from '@vn-propane/shared/client';
import { Badge } from '@/components/ui/badge';
import { formatPrice } from '@/lib/utils';
import {
  Truck,
  Clock,
  CheckCircle,
  CheckCircle2,
  XCircle,
  Package,
  User,
  Phone,
  MapPin,
  CreditCard,
} from 'lucide-react';

type FilterStatus = 'all' | OrderStatus;

export default function GasStationDeliveries() {
  const { t, i18n } = useTranslation('admin');
  const { id: orgId } = useParams<{ id: string }>();
  const { station, organization } = useGasStation();
  const [orders, setOrders] = useState<OrderSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterStatus>('all');

  const statusConfig: Record<
    OrderStatus,
    { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive'; icon: typeof Clock }
  > = {
    pending: { label: t('stationDeliveries.statusPending'), variant: 'secondary', icon: Clock },
    assigned: { label: t('stationDeliveries.statusAssigned'), variant: 'default', icon: Truck },
    in_progress: { label: t('stationDeliveries.statusInProgress'), variant: 'default', icon: Truck },
    arrived: { label: t('stationDeliveries.statusArrived'), variant: 'default', icon: Truck },
    delivered: { label: t('stationDeliveries.statusDelivered'), variant: 'outline', icon: CheckCircle },
    completed: { label: t('stationDeliveries.statusCompleted'), variant: 'outline', icon: CheckCircle2 },
    cancelled: { label: t('stationDeliveries.statusCancelled'), variant: 'destructive', icon: XCircle },
  };

  function formatDate(dateString: string) {
    const locale = i18n.language === 'vi' ? 'vi-VN' : 'en-US';
    return new Date(dateString).toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  const fetchOrders = useCallback(async () => {
    if (!orgId || !station) return;
    setLoading(true);
    try {
      const result = await listOrders({ path: { org_id: orgId } });
      if (result.data) {
        // Filter to this gas station's orders
        const stationOrders = result.data.data.filter(
          (o) => o.gas_station_name === station.name
        );
        setOrders(stationOrders);
      }
    } catch (err) {
      console.error('Failed to load orders:', err);
    } finally {
      setLoading(false);
    }
  }, [orgId, station]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const filteredOrders = filter === 'all' ? orders : orders.filter((o) => o.status === filter);

  const statusCounts = orders.reduce(
    (acc, o) => {
      acc[o.status] = (acc[o.status] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  if (loading) {
    return (
      <div className="flex justify-center p-8 text-muted-foreground">{t('stationDeliveries.loading')}</div>
    );
  }

  const filterButtons: { value: FilterStatus; label: string }[] = [
    { value: 'all', label: t('stationDeliveries.all', { count: orders.length }) },
    { value: 'pending', label: t('stationDeliveries.pending', { count: statusCounts['pending'] || 0 }) },
    { value: 'assigned', label: t('stationDeliveries.assigned', { count: statusCounts['assigned'] || 0 }) },
    { value: 'in_progress', label: t('stationDeliveries.inProgress', { count: statusCounts['in_progress'] || 0 }) },
    { value: 'delivered', label: t('stationDeliveries.delivered', { count: statusCounts['delivered'] || 0 }) },
    { value: 'cancelled', label: t('stationDeliveries.cancelled', { count: statusCounts['cancelled'] || 0 }) },
  ];

  return (
    <div className="space-y-4">
      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2">
        {filterButtons.map((btn) => (
          <button
            key={btn.value}
            onClick={() => setFilter(btn.value)}
            className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
              filter === btn.value
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-background text-muted-foreground border-border hover:bg-muted'
            }`}
          >
            {btn.label}
          </button>
        ))}
      </div>

      {/* Orders list */}
      {filteredOrders.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>{filter === 'all' ? t('stationDeliveries.noOrdersYet') : t('stationDeliveries.noOrdersWithStatus', { status: statusConfig[filter as OrderStatus]?.label || filter })}</p>
        </div>
      ) : (
        <div className="border rounded-lg divide-y">
          {filteredOrders.map((order) => {
            const config = statusConfig[order.status] || statusConfig.pending;
            const StatusIcon = config.icon;
            return (
              <Link
                key={order.id}
                to={`/organizations/${orgId}/orders/${order.id}`}
                className="block p-4 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm font-mono">
                        #{order.id.slice(-8).toUpperCase()}
                      </span>
                      <Badge variant={config.variant} className="flex items-center gap-1">
                        <StatusIcon className="w-3 h-3" />
                        {config.label}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      {order.customer_name && (
                        <div className="flex items-center gap-1">
                          <User className="w-3.5 h-3.5" />
                          <span>{order.customer_name}</span>
                        </div>
                      )}
                      {order.customer_phone && (
                        <div className="flex items-center gap-1">
                          <Phone className="w-3.5 h-3.5" />
                          <span>{order.customer_phone}</span>
                        </div>
                      )}
                      {order.driver_name ? (
                        <div className="flex items-center gap-1">
                          <Truck className="w-3.5 h-3.5" />
                          <span>{order.driver_name}</span>
                        </div>
                      ) : order.status === 'pending' ? (
                        <div className="flex items-center gap-1 text-amber-600">
                          <Truck className="w-3.5 h-3.5" />
                          <span>{t('stationDeliveries.noDriverAssigned')}</span>
                        </div>
                      ) : null}
                    </div>
                    {order.customer_address && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                        <MapPin className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{order.customer_address}</span>
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground mt-1">
                      {t('stationDeliveries.item', { count: order.item_count })} &middot; {formatDate(order.created_at)}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-medium">
                      {formatPrice(order.total_amount.toString(), organization.currency)}
                    </div>
                    {order.is_paid ? (
                      <div className="flex items-center gap-1 text-xs text-green-600 justify-end mt-0.5">
                        <CreditCard className="w-3 h-3" />
                        <span>{t('stationDeliveries.paid')}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-xs text-amber-600 justify-end mt-0.5">
                        <CreditCard className="w-3 h-3" />
                        <span>{t('stationDeliveries.unpaid')}</span>
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
